import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getFiscalYearLabel,
  isOwner,
  SEED_BUDGET_CODES,
  sumDollars,
  type NewOwnedBudgetCode,
} from '@budgetmonkey/shared'
import { useFiscalYear } from '@/lib/fiscalYear'
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Money,
} from '@/components/ui'
import { useBudgetCodes, useCreateBudgetCodesBatch } from '@/features/budgetCodes/hooks'
import { useUpdateUserProfile, useUserProfile } from '@/features/user/hooks'

/**
 * First-run setup — REQUIREMENTS.md §13 Phase 1.
 *
 * Loads the eleven codes from reference/budget-codes.csv with beginning
 * balances filled in here, rather than seeding silently on first login. Two
 * reasons: the CSV deliberately omits balances because they change every year,
 * and a silent seed would write eleven zero-balance codes that look correct
 * and quietly aren't.
 *
 * Idempotent and re-runnable — this is also the July 1 rollover flow, where
 * the same eleven codes get new balances for a new fiscal year (§9 rollover
 * note). Codes already present for the target year are skipped, not
 * duplicated.
 */
export function SetupScreen() {
  const navigate = useNavigate()
  // The selected year, not today's. This screen is also the rollover flow, so
  // setting up from the 25-26 view must build 25-26 codes — not silently seed
  // the current year (which is what sent Matt here: setup from last year kept
  // offering to create *this* year's codes).
  const fiscalYear = useFiscalYear()

  const { data: existingCodes = [], isLoading } = useBudgetCodes(fiscalYear)
  const { data: profile, isLoading: profileLoading } = useUserProfile()
  const createBatch = useCreateBudgetCodesBatch(fiscalYear)
  const updateProfile = useUpdateUserProfile()

  const [balances, setBalances] = useState<Record<string, string>>({})
  // `undefined` means "not edited yet, show the stored value". Using '' as the
  // initial state would make the field impossible to clear, since an empty
  // edit would fall back to the stored value on every render.
  const [cardholderName, setCardholderName] = useState<string>()
  const [department, setDepartment] = useState<string>()
  const [touched, setTouched] = useState(false)
  const cardholderRef = useRef<HTMLInputElement>(null)
  const departmentRef = useRef<HTMLInputElement>(null)

  const resolvedCardholder = cardholderName ?? profile?.cardholderName ?? ''
  const resolvedDepartment = department ?? profile?.department ?? ''

  const alreadyLoaded = useMemo(
    () => new Set(existingCodes.map((c) => c.code)),
    [existingCodes],
  )
  /**
   * The seed list is *Matt's* eleven codes, read from his own account setup.
   * Offering them to a colleague would hand them someone else's Travel and
   * Prof. Dev. codes with balance boxes beside them — nothing of his data
   * would leak, since they would be copies in their own account, but they
   * would be the wrong codes and confidently presented.
   *
   * So the seed is the owner's shortcut, and everyone else enters their own.
   */
  const seeds = isOwner(profile?.email) ? SEED_BUDGET_CODES : []
  const pending = seeds.filter((c) => !alreadyLoaded.has(c.code))
  const skipped = seeds.length - pending.length
  const bringsOwnCodes = seeds.length === 0

  const total = sumDollars(
    pending.map((c) => Number(balances[c.code] ?? '')).filter((n) => !Number.isNaN(n)),
  )
  const missingBalances = pending.filter(
    (c) => (balances[c.code] ?? '').trim() === '',
  ).length

  const canSubmit =
    (pending.length > 0 || bringsOwnCodes) &&
    // The profile document is created lazily on first read. Submitting before
    // that resolves would patch a document that doesn't exist yet.
    profile !== undefined &&
    resolvedCardholder.trim() !== '' &&
    resolvedDepartment.trim() !== ''

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setTouched(true)

    // The submit button stays enabled even when the form is incomplete. A
    // disabled button that never explains itself is a dead end: the required
    // fields sit at the top of a long page, and the validation messages only
    // appear once a submit has been attempted — which a disabled button
    // prevents. Instead, submit, show the errors, and move focus to the first
    // one so it's on screen.
    if (!canSubmit) {
      const firstInvalid =
        resolvedCardholder.trim() === ''
          ? cardholderRef.current
          : resolvedDepartment.trim() === ''
            ? departmentRef.current
            : null
      firstInvalid?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      firstInvalid?.focus({ preventScroll: true })
      return
    }

    const inputs: NewOwnedBudgetCode[] = pending.map((seed) => ({
      code: seed.code,
      displayName: seed.displayName,
      shortLabel: seed.shortLabel,
      fund: seed.fund,
      category: seed.category,
      aliases: seed.aliases,
      // Blank means zero. A code with no money is still a real code — grants
      // sometimes arrive funded later.
      beginningBalance: Number(balances[seed.code] ?? '') || 0,
      alertThresholdPercent: 80,
    }))

    await updateProfile.mutateAsync({
      cardholderName: resolvedCardholder.trim(),
      department: resolvedDepartment.trim(),
    })
    if (inputs.length > 0) await createBatch.mutateAsync(inputs)
    // Straight to the codes screen when there is nothing seeded to load —
    // that is the next thing they have to do, and the dashboard would show
    // an empty state telling them so.
    void navigate(bringsOwnCodes ? '/codes' : '/')
  }

  if (isLoading || profileLoading) {
    return (
      <p className="text-slate-500" role="status">
        Loading…
      </p>
    )
  }

  const error = createBatch.error?.message ?? updateProfile.error?.message

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Set up {getFiscalYearLabel(fiscalYear)}
        </h1>
        <p className="text-slate-600">
          {bringsOwnCodes
            ? "Two things for the packet header, then you'll add your own budget codes and their beginning balances."
            : `Your ${seeds.length} budget codes, ready to load. Enter this year's beginning balances — everything else can be changed later.`}
        </p>
      </header>

      {error !== undefined && <ErrorNote>{error}</ErrorNote>}

      {pending.length === 0 && !bringsOwnCodes ? (
        <Card className="space-y-3">
          <p className="font-medium">
            All {seeds.length} codes are already set up for{' '}
            {getFiscalYearLabel(fiscalYear)}.
          </p>
          <Button type="button" variant="primary" onClick={() => void navigate('/codes')}>
            Go to budget codes
          </Button>
        </Card>
      ) : (
        <>
          <Card className="space-y-5">
            <div>
              <h2 className="font-medium">For the reconciliation packet</h2>
              <p className="text-sm text-slate-600">
                Printed on every log sheet header, so enter them exactly as the
                business office expects.
              </p>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                label="Cardholder name"
                required
                error={
                  touched && resolvedCardholder.trim() === '' ? 'Required.' : undefined
                }
              >
                <Input
                  ref={cardholderRef}
                  value={resolvedCardholder}
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder={bringsOwnCodes ? 'Your name as the office knows it' : 'Matthew Callison'}
                  invalid={touched && resolvedCardholder.trim() === ''}
                />
              </Field>
              <Field
                label="Department / building"
                required
                error={
                  touched && resolvedDepartment.trim() === '' ? 'Required.' : undefined
                }
              >
                <Input
                  ref={departmentRef}
                  value={resolvedDepartment}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder={bringsOwnCodes ? 'Your department or building' : 'Innovation and Strategic Partnerships'}
                  invalid={touched && resolvedDepartment.trim() === ''}
                />
              </Field>
            </div>
          </Card>

          {skipped > 0 && (
            <p className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-700">
              {skipped} of these codes {skipped === 1 ? 'is' : 'are'} already set up for{' '}
              {getFiscalYearLabel(fiscalYear)} and will be left alone.
            </p>
          )}

          {bringsOwnCodes ? (
            <Card className="space-y-3">
              <h2 className="font-medium">Your budget codes</h2>
              <p className="text-sm text-slate-600">
                Add each code you spend against, with the amount authorized for{' '}
                {getFiscalYearLabel(fiscalYear)}. You can add more at any time,
                and change a balance later — the dashboard's remaining figure is
                only as right as what you put in here.
              </p>
              <p className="text-sm text-slate-600">
                Saving takes you to your budget codes. If you have your
                building's budget spreadsheet, import it there — every code and
                amount comes in at once, and you check them before anything is
                saved. Otherwise, add them one at a time.
              </p>
            </Card>
          ) : (
          <Card className="space-y-4">
            <div className="flex items-baseline justify-between gap-4">
              <h2 className="font-medium">Beginning balances</h2>
              <p className="text-sm text-slate-500">
                {missingBalances > 0
                  ? `${missingBalances} blank — they'll be set to $0`
                  : 'All filled in'}
              </p>
            </div>

            <ul className="divide-y divide-slate-100">
              {pending.map((seed) => (
                <li
                  key={seed.code}
                  className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">
                        {seed.shortLabel}
                      </span>
                      {seed.fund === 'grant' && <Badge tone="ok">Grant</Badge>}
                      {seed.aliases.map((alias) => (
                        <span
                          key={alias}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                        >
                          {alias}
                        </span>
                      ))}
                    </div>
                    <p className="font-mono text-xs text-slate-500">{seed.code}</p>
                  </div>

                  <div className="w-40">
                    <Input
                      value={balances[seed.code] ?? ''}
                      onChange={(e) =>
                        setBalances((prev) => ({ ...prev, [seed.code]: e.target.value }))
                      }
                      placeholder="0.00"
                      inputMode="decimal"
                      aria-label={`Beginning balance for ${seed.shortLabel}`}
                      className="text-right"
                    />
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex items-baseline justify-between border-t border-slate-200 pt-4">
              <span className="font-medium">Total authorized</span>
              <Money value={total} className="text-lg font-semibold" />
            </div>
          </Card>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap gap-3">
              <Button
                type="submit"
                variant="primary"
                disabled={createBatch.isPending || updateProfile.isPending}
              >
                {createBatch.isPending || updateProfile.isPending
                  ? 'Setting up…'
                  : bringsOwnCodes
                    ? 'Save and add my codes'
                    : `Load ${pending.length} budget codes`}
              </Button>
              {!bringsOwnCodes && (
                <Button type="button" onClick={() => void navigate('/codes')}>
                  Skip for now
                </Button>
              )}
            </div>

            {/* Named up front rather than only after a failed submit — the
                fields in question are a long scroll away at the top. */}
            {!canSubmit && (
              <p className="text-sm text-slate-600">
                Still needed at the top of this page:{' '}
                {[
                  resolvedCardholder.trim() === '' ? 'cardholder name' : null,
                  resolvedDepartment.trim() === '' ? 'department / building' : null,
                ]
                  .filter(Boolean)
                  .join(' and ')}
                .
              </p>
            )}
          </div>
        </>
      )}
    </form>
  )
}
